import { HttpClient, HttpContext } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { CommandService } from '@helios/commands'
import {
  ERROR_INTERCEPTOR_BYPASS,
  HANDLE_ERROR_AND_THROW,
  HELIOS_COMMAND_HTTP_TOKEN,
} from '@helios/utils'
import { Observable } from 'rxjs'
import {
  FlexTransactionActionRequest,
  FlexTransactionActionResponse,
} from '../models/transaction-action.model'
import { TransactionActionsService } from './transaction-actions.service'

/**
 * The write endpoint. Resolved against `<base href="/helios/">` exactly like `QUERY_URL`
 * (`http-workbench-data-source.ts`), and behind the same host: for local beta dev the
 * `/helios/helios-flex-transactions/**` proxy entry must be added ahead of the generic
 * `/helios/helios-*` catch-all — a local-dev-only edit that is NOT committed (CLAUDE.md:
 * "Remember to revert proxy.conf.beta.json before committing").
 */
export const ACTIONS_URL = 'helios-flex-transactions/flex-transactions/actions'

/** Panel name stamped on the command headers, as it should read in the commands drawer. */
export const PANEL_NAME = 'Transactions 2.0'

/**
 * Statuses this endpoint uses to report a *workflow* outcome rather than an application defect, and
 * which are therefore kept out of the global error drawer.
 *
 *  - `400` — every validation this endpoint enforces (single account, no Custody+Trading mix, no
 *    group merge, no already-matched pair). Each one is a rule the user broke, reported back with a
 *    message naming which; a persistent defect record for it is noise.
 *  - `409` — the same rows changed concurrently. Retryable by the user, not a defect either.
 *
 * Statuses NOT listed here (5xx, or a status-0 transport failure) are left to the drawer on purpose:
 * those are defects, and the errorId recorded there is what a bug report is keyed on.
 */
export const WORKFLOW_ERROR_STATUSES: readonly number[] = [400, 409]

/**
 * Live implementation of the actions seam, copying the house pattern for a command-issuing POST from
 * `clearwater-transactions.service.ts` and `bulk-clearwater-transactions.service.ts`:
 *
 *  - `HANDLE_ERROR_AND_THROW` rethrows the `HttpErrorResponse` to the caller *in addition to* the
 *    global error drawer, not instead of it: `error.interceptor.ts` runs `handleStandardApiError`
 *    — which dispatches `reportError` into the drawer — unconditionally BEFORE it honours this
 *    token. It is what lets the panel render the server's own message on the statuses that are not
 *    bypassed below, where a drawer defect record is the right outcome as well as an in-panel one;
 *  - `ERROR_INTERCEPTOR_BYPASS` with {@link WORKFLOW_ERROR_STATUSES} is the only short-circuit
 *    ahead of `handleStandardApiError`, so it is the one thing that keeps ordinary workflow
 *    validation out of the drawer. The bypass still throws, so the caller sees these statuses
 *    exactly as before — the list governs drawer logging, not caller visibility. Same pattern, for
 *    the same reason, as `bulk-clearwater-transactions.service.ts` and
 *    `bulk-external-transactions.service.ts` bypassing `[503]`. Incidentally it also stops the
 *    body-less synthetic 400 that `commands-loading.interceptor.ts` throws for a session with
 *    pending approvals from reaching the drawer as a spurious `Unknown API Error` on top of the
 *    toast that interceptor already raises (`ErrorInterceptor` is registered ahead of
 *    `CommandsLoadingInterceptor` in `app.module.ts`, so it sees that synthetic error unwinding);
 *  - `HELIOS_COMMAND_HTTP_TOKEN` drives the commands-drawer loading state
 *    (`commands-loading.interceptor.ts`);
 *  - `generateTemporaryHeaders` stamps `helios-internal-cmd-panel` / `helios-internal-cmd-action` so
 *    the command shows up attributed to this panel and this verb.
 *
 * `helios-session-id` is deliberately NOT set here: the app's SessionInterceptor attaches it
 * globally, and no call site in this panel sets it.
 */
@Injectable()
export class HttpTransactionActionsService extends TransactionActionsService {
  constructor(
    private readonly http: HttpClient,
    private readonly commandService: CommandService,
  ) {
    super()
  }

  submit(
    request: FlexTransactionActionRequest,
  ): Observable<FlexTransactionActionResponse> {
    return this.http.post<FlexTransactionActionResponse>(ACTIONS_URL, request, {
      headers: this.commandService.generateTemporaryHeaders(
        PANEL_NAME,
        request.actionType,
      ),
      context: new HttpContext()
        // Copied per request: the token holds the array by reference, and the exported constant is
        // shared module state.
        .set(ERROR_INTERCEPTOR_BYPASS, [...WORKFLOW_ERROR_STATUSES])
        .set(HANDLE_ERROR_AND_THROW, true)
        .set(HELIOS_COMMAND_HTTP_TOKEN, true),
    })
  }
}
